import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';

// NOTE ON CONTENT: fees, processing times and document rules below are
// realistic placeholders matching the UAE's tourist e-visa structure.
// Confirm current figures with the ICP (Federal Authority for Identity,
// Citizenship, Customs & Port Security) or your UAE visa partner before
// publishing, and keep this file as the single place to update them.

const plans: VisaPlan[] = [
  { id: 'ae-30s', name: '30-Day Tourist Visa', entryType: 'Single Entry', validity: 'Use within 60 days of issue', stay: 'Up to 30 days in the UAE', processing: '2–4 business days', price: '260', currency: 'AED' },
  { id: 'ae-30m', name: '30-Day Tourist Visa', entryType: 'Multiple Entry', validity: 'Use within 60 days of issue', stay: 'Up to 30 days per visit', processing: '3–5 business days', price: '450', currency: 'AED', featured: true },
  { id: 'ae-60', name: '60-Day Tourist Visa', entryType: 'Single Entry', validity: 'Use within 60 days of issue', stay: 'Up to 60 days in the UAE', processing: '3–5 business days', price: '650', currency: 'AED' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date, with at least one blank page.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white background, taken within the last 6 months.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward flight booking showing your exit from the UAE.' },
  { label: 'Hotel booking or host details', detail: 'A confirmed hotel reservation, or your host\'s Emirates ID and tenancy details if staying privately.' },
  { label: 'Travel insurance (recommended)', detail: 'Not always mandatory, but strongly recommended and requested for some nationalities.' },
];

const goodToKnow: string[] = [
  'Multiple-entry visas suit travelers combining the UAE with side trips to Oman or elsewhere in the Gulf.',
  'Overstaying even by a day carries daily fines, so we build in a buffer when confirming your exit date.',
  'Some nationalities are eligible for visa-on-arrival — ask us first, as it may save you both time and cost.',
  'Children under 18 are typically issued a visa linked to an accompanying adult\'s application.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Send your passport scan, photo and travel dates through our secure form.' },
  { title: 'We review & submit', text: 'Our team checks your file against current ICP requirements before filing.' },
  { title: 'Track your status', text: 'We keep you updated by WhatsApp or email while your visa is processed.' },
  { title: 'Receive your eVisa', text: 'Download the approved visa and carry a printed copy alongside your passport.' },
];

const faqs: FaqItem[] = [
  { question: 'Can I convert a tourist visa to a different visa type inside the UAE?', answer: 'Some visa status changes are possible without leaving the country, depending on your nationality and situation. Ask our team for a same-day assessment.' },
  { question: 'Is the multiple-entry visa worth it for a single trip?', answer: 'If you\'re only visiting the UAE once, the single-entry option is usually more cost-effective — we\'ll recommend the right fit based on your itinerary.' },
  { question: 'Do I need a UAE visa if I\'m just transiting through Dubai?', answer: 'Short airside transits typically don\'t require a visa, but if you\'re leaving the airport, standard visa rules apply. Tell us your layover plan and we\'ll confirm.' },
  { question: 'What happens if my flight is delayed past my visa validity?', answer: 'Contact us as soon as you know — we can advise on extension options or help you avoid an overstay situation.' },
];

export default function VisaUaePage() {
    const { plans, loading, error } = useVisaPacksByCountry('ARE');
  return (
    <VisaPageLayout
      countryName="UAE"
      flagEmoji="🇦🇪"
      heroEyebrow="United Arab Emirates"
      heroImage="https://images.unsplash.com/photo-1748373448914-1d7f882700e2?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="UAE"
      heroTitleTail="visa, sorted before you pack"
      heroSubtitle="Dubai's skyline, Abu Dhabi's culture, and everything between — we handle the paperwork for your UAE tourist visa so you can focus on the itinerary."
      trustStats={[
        { value: '2–5', label: 'Business Days' },
        { value: '97%', label: 'Approval Rate' },
        { value: '6,200+', label: 'UAE Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Send us your documents and travel dates — most complete files are submitted to the ICP within one business day."
    />
  );
}